import { keyframes } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';

import { REDESIGN_HEADER_HEIGHT_CSS_VARIABLE } from 'src/components/layout/redesign/header-shared/header-styling';
import type { Styling } from 'src/context/styling';
import { BuildType } from 'src/enums/build/build-type';
import { borderRadius, zIndexes } from 'src/styling/constants';
import mq, { mqReducedMotion } from 'src/styling/media-queries';

export const createVideoAndImageSliderStyle = (
  styling: Styling,
  isTabletOrSmaller: boolean,
  isPortraitOrientation: boolean,
  noSliderControlMargin: boolean,
  isFullWidthLayout: boolean,
  isMeridian: boolean,
  isPopup?: boolean
) => {
  const contentWidthFullWidthLayout = `75vw !important`;
  const contentHeightFullWidthLayout = 'auto';
  const contentAspectRatioFullWidthLayout = 4 / 1.6;
  return {
    mainContainer: { width: '100%', paddingBottom: ['2.94rem', 0] },
    mainWrapperCSS: mq({
      'overflow': 'unset',
      '.slider-control-wrap': {
        marginBottom: noSliderControlMargin ? 0 : ['3.75rem', 0],
      },
      '& .custom-navigation-wrapper': {
        justifyContent: 'center',
        maxWidth: ['unset', '60rem'],
      },
      '& swiper-slide': {
        'opacity': '1',
        'transition': 'opacity 0.9s',
        '& .gallery, & .video-gallery': {
          position: 'relative',
          transform: 'scale(1)',
          transition: 'transform 0.9s',
        },
      },
      '& swiper-slide:not(.swiper-slide-active)': {
        'opacity': '0.2',
        'background': 'rgba(255, 255, 255, 0)',
        '& .gallery, & .video-gallery': {
          transform: isTabletOrSmaller && isPortraitOrientation ? 'scale(0.8351)' : 'scale(1)',
        },
        '& .gallery, & .gallery-title': {
          display: 'flex',
          alignItems: 'center',
          borderRadius: isFullWidthLayout || isMeridian ? 'unset' : borderRadius.image.medium,
        },
      },
    }),
    videoContainer: [
      mq({
        width: isFullWidthLayout ? contentWidthFullWidthLayout : ['calc(100vw - 4rem)', '68.5rem'],
        position: 'relative',
        borderRadius: isFullWidthLayout || isMeridian ? 'unset' : borderRadius.image.medium,
        overflow: 'hidden',
        maxWidth: ['29.1875rem', 'none'],
        marginTop: isPopup ? 'unset' : '2rem',
      }),
      mq.withSmallTablet({
        height: isFullWidthLayout
          ? contentHeightFullWidthLayout
          : [
              isPortraitOrientation ? '29.1875rem' : '16.25rem',
              isPortraitOrientation ? '29.1875rem' : '19.375rem',
              '42.58438rem',
            ],
      }),
      isFullWidthLayout && { aspectRatio: contentAspectRatioFullWidthLayout },
    ],
    videoBackground: mq({
      height: '100%',
      width: '100%',
      position: 'absolute',
      backgroundColor: 'black',
      borderRadius: isFullWidthLayout || isMeridian ? 'unset' : borderRadius.image.medium,
      top: '50%',
      transform: 'translateY(-50%)',
    }),
    videoOverlay: mq.withSmallTablet({
      position: 'absolute',
      borderRadius: isFullWidthLayout || isMeridian ? 'unset' : borderRadius.image.medium,
      top: isFullWidthLayout ? '25%' : 0,
      bottom: 'unset',
      width: ['91%', '91%', '95%'],
      height: isFullWidthLayout ? '50%' : ['59%', '69%', '81%'],
      backgroundColor: 'transparent',
      pointerEvents: 'unset',
    }),
    videoIframe: mq([
      {
        width: isFullWidthLayout ? '100%' : ['calc(100% - 0.0625rem)', 'calc(100% - 0.0625rem)'],
        height: isFullWidthLayout
          ? contentHeightFullWidthLayout
          : ['calc(100% + 0.0625rem)', 'calc(100% + 0.0625rem)'],
        position: 'absolute',
        top: ['50%', '50%'],
        bottom: ['unset', 'unset'],
        left: ['unset', 'unset'],
        transform: ['translateY(-50%)', 'translateY(-50%)'],
      },
      isFullWidthLayout && { aspectRatio: contentAspectRatioFullWidthLayout },
    ]),
    imageStyle: [
      mq.withSmallTablet({
        'height': isFullWidthLayout
          ? contentHeightFullWidthLayout
          : [
              isPortraitOrientation ? '29.1875rem' : '16.25rem',
              isPortraitOrientation ? '29.1875rem' : '19.375rem',
              '42.58438rem',
            ],
        'width': isFullWidthLayout
          ? contentWidthFullWidthLayout
          : [
              isPortraitOrientation ? '16.25rem' : '29.1875rem',
              isPortraitOrientation ? '19.375rem' : '29.1875rem',
              '68.5rem',
            ],
        'borderRadius': isFullWidthLayout || isMeridian ? 'unset' : borderRadius.image.medium,
        '& img': {
          position: 'absolute',
          top: '0',
          borderRadius: isFullWidthLayout || isMeridian ? 'unset' : borderRadius.image.medium,
        },
      }),
      isFullWidthLayout && { aspectRatio: contentAspectRatioFullWidthLayout },
    ],
    imageTitle: [
      mq({
        display: 'flex',
        fontSize: ['0.875rem', '1.1875rem'],
        color: styling.colors.capeCod,
        textAlign: 'left',
        marginTop: ['2.1rem', '1rem'],
        flexWrap: 'wrap',
        lineHeight: '145%',
        minHeight: ['1.26875rem', '1.721875rem'],
        marginLeft: isFullWidthLayout ? [0, '1.25rem'] : 0,
      }),
      mq.withSmallTablet({
        width: ['16.25rem', '19.375rem', '68.5rem'],
      }),
      isMeridian && {
        fontSize: ['1rem', '1.1875rem'],
        marginTop: ['2.5rem', '1.5rem'],
      },
    ],
  };
};

const titleFadeAnimation = keyframes`
    0% {
        opacity: 0;
        transform: translateY(30%);
    }
    100% {
        opacity: 1;
        transform: translateY(0);
    }
`;

const preTitleFadeAnimation = keyframes`
    0% {
        opacity: 0;
        transform: translateY(-50%);
    }
    100% {
        opacity: 1;
        transform: translateY(0);
    }
`;

const videoPlayerBrandTheme = (
  styling: Styling,
  buildType: BuildType,
  titleMinHeight: string[],
  titleFontSize: string[]
) => {
  switch (buildType) {
    case BuildType.MERIDIAN:
      return {
        titlePlaceHolder: mq({
          minHeight: titleMinHeight,
          marginTop: ['0.25rem', '0.94rem'],
          padding: ['0.5rem 1.25rem 0', 0],
        }),
        mainTitle: mq([
          styling.fonts.light,
          {
            textAlign: 'center',
            fontSize: titleFontSize,
            lineHeight: '100%',
            color: styling.colors.lightBackground,
          },
        ]),
        preTitleCardFontSize: ['1.1875rem', '1.5rem'],
        preTitleCardColor: styling.colors.darkGreen,
        preTitleCardFontStyle: styling.fonts.regular,
      };
    case BuildType.HARBOUR:
      return {
        titlePlaceHolder: mq({
          minHeight: [
            '3.75rem',
            `clamp(5rem, 10rem, calc(25vh - var(--${REDESIGN_HEADER_HEIGHT_CSS_VARIABLE})))`,
          ],
          marginTop: ['1.5rem', '2.5rem'],
          padding: ['0 1.25rem', 0],
        }),
        mainTitle: mq([
          styling.fonts.light,
          {
            textAlign: 'center',
            fontSize: ['3.75rem', 'clamp(5rem, 10rem, 25vh)'],
            lineHeight: '100%',
            color: styling.colors.lightBackground,
          },
        ]),
        preTitleCardFontSize: ['1.25rem', '1.375rem'],
        preTitleCardColor: styling.colors.capeCod,
        preTitleCardFontStyle: styling.fonts.light,
      };
    default:
      return {
        titlePlaceHolder: mq({
          minHeight: [
            '3.75rem',
            `clamp(5rem, 10rem, calc(25vh - var(--${REDESIGN_HEADER_HEIGHT_CSS_VARIABLE})))`,
          ],
          marginTop: ['1.5rem', '2.5rem'],
          padding: ['0 1.25rem', 0],
        }),
        mainTitle: mq({
          textAlign: 'center',
          fontSize: ['3.75rem', 'clamp(5rem, 10rem, 25vh)'],
          lineHeight: '100%',
          color: styling.colors.lightBackground,
        }),
        preTitleCardFontSize: ['1.1875rem', '1.5rem'],
        preTitleCardColor: styling.colors.darkGreen,
        preTitleCardFontStyle: styling.fonts.regular,
      };
  }
};

export const createVideoPlayerStyling = (
  styling: Styling,
  isVideoId: boolean,
  buildType: BuildType,
  isTitleLarge: boolean
) => {
  const titleMinHeight = isTitleLarge
    ? [
        '3.25rem',
        `clamp(5rem, 9.375rem, calc(25vh - var(--${REDESIGN_HEADER_HEIGHT_CSS_VARIABLE})))`,
      ]
    : [
        '2.375rem',
        `clamp(4.5rem, 5rem, calc(15vh - var(--${REDESIGN_HEADER_HEIGHT_CSS_VARIABLE})))`,
      ];
  const titleFontSize = isTitleLarge
    ? ['3.25rem', 'clamp(5rem, 9.375rem, 25vh)']
    : ['2.375rem', 'clamp(4.5rem, 5rem, 15vh)'];

  const theme = videoPlayerBrandTheme(styling, buildType, titleMinHeight, titleFontSize);
  return {
    wrapperStyle: mq({
      height: ['38.875rem', '52.188rem'],
      position: 'relative',
      overflow: 'hidden',
      maxHeight: isVideoId
        ? [`calc(100vh - var(--${REDESIGN_HEADER_HEIGHT_CSS_VARIABLE}))`, 'unset']
        : 'unset',
    }),
    controlsWrapperStyle: {
      position: 'absolute',
      bottom: '2.5rem',
      left: 0,
      right: 0,
      maxWidth: '92.25rem',
      margin: '0 auto',
      width: '100%',
      height: '100%',
      zIndex: zIndexes.overlay,
    },
    playControlStyle: mq({
      position: 'absolute',
      bottom: 0,
      left: ['1.25rem', '3.75rem'],
      cursor: 'pointer',
    }),
    preTitleCard: mq([
      theme.preTitleCardFontStyle,
      {
        backgroundColor: styling.colors.lightBackground,
        color: theme.preTitleCardColor,
        fontSize: theme.preTitleCardFontSize,
        lineHeight: ['0.8125rem', '100%'],
        padding: '0.75rem 1.5rem',
        letterSpacing: ['0.3125rem', '0.625rem'],
        paddingLeft: ['1.8125rem', '2.125rem'], // padding right + letter spacing
        textTransform: 'uppercase',
        display: 'inline-block',
      },
    ]),
    preTitleCardAnimation: mqReducedMotion({
      opacity: [0, 1], // wait for the animation to show it
      animation: [`${preTitleFadeAnimation} 0.8s ease-out 0.1s`, 'none'],
      animationFillMode: 'both',
    }),
    contentWrapper: {
      maxHeight: '100%',
      position: 'absolute',
      width: '100%',
      left: 0,
      right: 0,
      bottom: 0,
      textAlign: 'center',
      maxWidth: '84.75rem',
      margin: '0 auto',
      top: '50%',
      transform: 'translateY(-50%)',
    },
    titlePlaceHolder: theme.titlePlaceHolder,
    titleAnimation: mqReducedMotion({
      opacity: [0, 1], // wait for the animation to show it
      animation: [`${titleFadeAnimation} 0.8s ease-out 0.1s`, 'none'],
      animationFillMode: 'both',
    }),
    mainTitle: theme.mainTitle,
    arrowWrapper: mq({ width: '100%', height: '8.75rem', marginTop: ['3.75rem', '7.5rem'] }),
    eyebrowPreTitleWrapper: mq([
      styling.fonts.regular,
      {
        display: 'flex',
        flexWrap: 'nowrap',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: ['0.875rem', '1.25rem'],
        color: 'white',
        padding: ['0.25rem 0', '0.625rem'],
      },
    ]),
    eyebrowPreTitleText: mq({
      padding: ['0 0.37rem', '0 0.62rem'],
      lineHeight: ['105%', '82%'],
      letterSpacing: ['0.3125rem', '0.3125rem'],
      textTransform: 'uppercase',
    }),
    eyebrowPreTitleIcon: mq({
      width: ['1.875rem', '4.0625rem'],
      height: ['0.125rem', '0.21681rem'],
      flexShrink: 0,
      aspectRatio: ['15/1', 'unset'],
    }),
    subtitle: mq([
      styling.fonts.light,
      {
        fontSize: ['1.5625rem', '2.5rem'],
        lineHeight: ['145%', '100%'],
        color: 'white',
        marginTop: ['0.62rem', '2.12rem'],
        padding: ['0 1.25rem', 0],
      },
    ]),
  } satisfies Record<string, CSSInterpolation>;
};

export type ContainerSize = { width: number; height: number };

export const calculateScaleForDifferentAspectRatio = (
  containerElement: HTMLElement | null,
  containerSize: ContainerSize
) => {
  const videoElement = containerElement?.getElementsByTagName('video')[0];
  if (!videoElement?.videoWidth || !videoElement.videoHeight) {
    return null;
  }

  const videoAspectRatio = videoElement.videoWidth / videoElement.videoHeight;

  if (videoAspectRatio >= 16 / 9) {
    return null;
  }

  const actualVideoWidth = videoElement.offsetHeight * videoAspectRatio;

  return actualVideoWidth < containerSize.width
    ? `${containerSize.width / actualVideoWidth}`
    : null;
};

export const createVideoWrapperStyles = (
  containerSize: ContainerSize | null,
  scale: string | undefined
): CSSInterpolation => {
  if (!containerSize) {
    return {};
  }

  const heightForWidth = (containerSize.width * 9) / 16;
  const widthForHeight = (containerSize.height * 16) / 9;

  const letterboxIsHorizontal = heightForWidth > containerSize.height;

  return letterboxIsHorizontal
    ? {
        width: '100%',
        height: heightForWidth,
        overflowY: 'clip',
        top: -(heightForWidth - containerSize.height) / 2,
        left: 0,
        scale,
      }
    : {
        height: '100%',
        width: widthForHeight,
        overflowX: 'clip',
        left: -(widthForHeight - containerSize.width) / 2,
        top: 0,
        scale,
      };
};
